import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import {
  BackofficeRole,
  EditableGroup,
  GroupListItem,
  GroupVisitor,
  GroupExhibitor,
  ReducedGroupVisitor,
  Sortable,
  Paginated,
  Searchable,
  PaginatedResponse,
} from "@ac-expo/api-types";
import groupQueryKeys from "../query-keys/group.query-keys";
import { organiserVisitorsQueryKeys } from "../query-keys";

const getMyGroups = async (
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>
) => {
  const { data } = await privateAxios.get<PaginatedResponse<GroupListItem>>(
    `/group/${as}`,
    {
      params: filters,
    }
  );
  return data;
};

export const useGetGroupsQuery = (
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>
) => {
  return useQuery({
    queryKey: [...groupQueryKeys.as(as), filters],
    queryFn: () => getMyGroups(as, filters),
  });
};

const getAllGroups = async (as: BackofficeRole) => {
  const { data } = await privateAxios.get<GroupListItem[] | null>(
    `/group/${as}/all`
  );
  return data;
};

export const useGetAllGroupsQuery = (as: BackofficeRole) => {
  return useQuery({
    queryKey: [...groupQueryKeys.as(as), "all"],
    queryFn: () => getAllGroups(as),
  });
};

const getSelectableVisitors = async (
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>
) => {
  const { data } = await privateAxios.get<PaginatedResponse<GroupVisitor>>(
    `/group/${as}/visitors`,
    {
      params: filters,
    }
  );
  return data;
};

export const useGetSelectableVisitorsQuery = (
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>,
  options?: Omit<
    UseQueryOptions<PaginatedResponse<GroupVisitor>, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: [...organiserVisitorsQueryKeys.all, as, filters],
    queryFn: () => getSelectableVisitors(as, filters),
    ...options,
  });
};

const getEditableGroup = async (id: string, as: BackofficeRole) => {
  const { data } = await privateAxios.get<EditableGroup | null>(
    `/group/${as}/${id}`
  );
  return data;
};

export const useGetEditableGroupQuery = (id: string, as: BackofficeRole) => {
  return useQuery({
    queryKey: groupQueryKeys.getAs(id, as),
    queryFn: () => getEditableGroup(id, as),
  });
};

const getGroupVisitors = async (groupIds: string[], as: BackofficeRole) => {
  const { data } = await privateAxios.get<ReducedGroupVisitor[]>(
    `/group/${as}/members?groupIds=${groupIds.join(",")}`
  );
  return data;
};

export const useGetGroupVisitorsQuery = (
  groupIds: string[],
  as: BackofficeRole
) => {
  return useQuery({
    queryKey: [...groupQueryKeys.all, as, ...groupIds],
    queryFn: () => getGroupVisitors(groupIds, as),
  });
};

const getAvailableVisitors = async (
  groupId: string,
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>
) => {
  const { data } = await privateAxios.get<PaginatedResponse<GroupVisitor>>(
    `/group/${as}/${groupId}/available-visitors`,
    {
      params: filters,
    }
  );
  return data;
};

export const useGetAvailableVisitorsQuery = (
  groupId: string,
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>
) => {
  return useQuery({
    queryKey: [
      ...groupQueryKeys.getAs(groupId, as),
      "available-visitors",
      filters,
    ],
    queryFn: () => getAvailableVisitors(groupId, as, filters),
  });
};

const getSelectableExhibitors = async (
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>
) => {
  const { data } = await privateAxios.get<PaginatedResponse<GroupExhibitor>>(
    `/group/${as}/exhibitors`,
    {
      params: filters,
    }
  );
  return data;
};

export const useGetSelectableExhibitorsQuery = (
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>,
  options?: Omit<
    UseQueryOptions<PaginatedResponse<GroupExhibitor>, Error>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: [...groupQueryKeys.all, as, "exhibitors", filters],
    queryFn: () => getSelectableExhibitors(as, filters),
    ...options,
  });
};

const getGroupExhibitors = async (groupIds: string[], as: BackofficeRole) => {
  const { data } = await privateAxios.get<GroupExhibitor[]>(
    `/group/${as}/exhibitor-members?groupIds=${groupIds.join(",")}`
  );
  return data;
};

export const useGetGroupExhibitorsQuery = (
  groupIds: string[],
  as: BackofficeRole
) => {
  return useQuery({
    queryKey: [...groupQueryKeys.all, as, "exhibitors", ...groupIds],
    queryFn: () => getGroupExhibitors(groupIds, as),
  });
};

const getAvailableExhibitors = async (
  groupId: string,
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>
) => {
  const { data } = await privateAxios.get<PaginatedResponse<GroupExhibitor>>(
    `/group/${as}/${groupId}/available-exhibitors`,
    {
      params: filters,
    }
  );
  return data;
};

export const useGetAvailableExhibitorsQuery = (
  groupId: string,
  as: BackofficeRole,
  filters: Sortable<Paginated<Searchable>>
) => {
  return useQuery({
    queryKey: [
      ...groupQueryKeys.getAs(groupId, as),
      "available-exhibitors",
      filters,
    ],
    queryFn: () => getAvailableExhibitors(groupId, as, filters),
  });
};
